import React, { ReactElement, useState } from 'react';
import {
  View, StyleSheet, Text, FlatList, TextInput, Pressable, ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FontAwesome } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import {
  Template, TemplateGroup, TemplateType, TemplateTypeTitles, UserGroup, UserPermission,
} from '../../../../constants/Types';
import TemplateIcon24 from '../../../../components/24pxIcons/TemplateIcon24';
import SearchIcon16 from '../../../../components/16pxIcons/SearchIcon16';
import PressableStyled from '../../../../components/PressableStyled';
import SelectionModal from '../../../../components/SelectionModal';
import EllipsisIcon from '../../../../components/EllipsisIcon';
import PlusIcon from '../../../../components/16pxIcons/PlusIcon';
import Button from '../../../../components/Button';
import AddTemplateModal from '../../../../components/AssetManagement/AddTemplateModal';
import SensorIcon from '../../../../components/24pxIcons/SensorIcon';
import MeterIcon from '../../../../components/24pxIcons/MeterIcon';
import AlertModal from '../../../../components/AlertModal';
import Style from '../../../../constants/Style';
import deleteTemplate from '../../../../requests/Assets/DeleteTemplate';
import {
  DELETE_TEMPLATE_FAILURE,
  DELETE_TEMPLATE_GROUP_SUCCESS,
} from '../../../../constants/AlertMessages';
import AddTemplateGroupModal from '../../../../components/AssetManagement/AddTemplateGroupModal';
import updateTemplateGroup from '../../../../requests/Assets/UpdateTemplateGroup';
import ModalContent from './components/ModalContent';
import TemplateDetailsScreen from './components/TemplateDetailsScreen';

export interface TemplateItem extends Template {
  templateGroupName: string,
}

interface TemplatesMobileViewProps {
  templates: TemplateItem[],
  templatesLoading: boolean,
  templateGroups: TemplateGroup[],
  canAddTemplate: boolean,
  canAddTemplateGroup: boolean,
  userGroups: UserGroup[],
  // eslint-disable-next-line no-unused-vars
  updateTemplateLocally: (template: Template, add: boolean, groupID: string) => void,
  // eslint-disable-next-line no-unused-vars
  updateTemplateGroupLocally: (...args: [string, string, boolean, boolean?]) => void,
  // eslint-disable-next-line no-unused-vars
  removeTemplateLocally: (id: string) => void,
  // eslint-disable-next-line no-unused-vars
  showScreenAlert: (title: string, message?: string, error?: boolean) => void,
}

type TemplateGroupActionType = 'add' | 'edit' | 'delete';

type TemplateActionType = 'edit' | 'delete';

interface PendingTemplateGroupAction {
  type: TemplateGroupActionType,
  templateGroup: TemplateGroup | null,
}

interface PendingTemplateAction {
  type: TemplateActionType,
  template: TemplateItem | null,
}

export default function TemplatesMobileView({
  templates,
  templatesLoading,
  templateGroups,
  canAddTemplate,
  canAddTemplateGroup,
  userGroups,
  updateTemplateLocally,
  updateTemplateGroupLocally,
  removeTemplateLocally,
  showScreenAlert,
}: TemplatesMobileViewProps) {
  const { top } = useSafeAreaInsets();
  const [templateSearchValue, setTemplateSearchValue] = useState<string>('');
  const [templateGroupSelectionShown, setTemplateGroupSelectionShown] = useState<boolean>(false);
  const [selectedTemplateGroup, setSelectedTemplateGroup] = useState<TemplateGroup | null>(null);
  const [addTemplateModalVisible, setAddTemplateModalVisible] = useState<boolean>(false);
  const [optionsSelectedTemplate, setOptionsSelectedTemplate] = useState<TemplateItem | null>(null);
  const [pendingTemplateAction,
    setPendingTemplateAction] = useState<PendingTemplateAction | null>(null);
  const [templateToUpdate, setTemplateToUpdate] = useState<TemplateItem | null>(null);
  const [templateDetailsShown, setTemplateDetailsShown] = useState<TemplateItem | null>(null);
  const [templateGroupToUpdate, setTemplateGroupToUpdate] = useState<TemplateGroup | null>(null);
  const [templateGroupToDelete, setTemplateGroupToDelete] = useState<TemplateGroup | null>(null);
  const [addTemplateGroupModalVisible, setAddTemplateGroupModalVisible] = useState<boolean>(false);
  const [pendingTemplateGroupAction,
    setPendingTemplateGroupAction] = useState<PendingTemplateGroupAction | null>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>('');
  const [deletingTemplate, setDeletingTemplate] = useState<boolean>(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);

  const showTemplateGroupSelection: () => void = () => setTemplateGroupSelectionShown(true);

  const hideTemplateGroupSelection: () => void = () => setTemplateGroupSelectionShown(false);

  const requestTemplateGroupAction = (
    type: TemplateGroupActionType,
    templateGroup: TemplateGroup | null = null,
  ) => {
    setPendingTemplateGroupAction({ type, templateGroup });
    hideTemplateGroupSelection();
  };

  const onTemplateGroupSelectionModalHide: () => void = () => {
    if (!pendingTemplateGroupAction) return;

    if (pendingTemplateGroupAction.type === 'add') {
      setAddTemplateGroupModalVisible(true);
    } else if (pendingTemplateGroupAction.type === 'edit'
      && pendingTemplateGroupAction.templateGroup) {
      setTemplateGroupToUpdate(pendingTemplateGroupAction.templateGroup);
    } else if (pendingTemplateGroupAction.type === 'delete'
      && pendingTemplateGroupAction.templateGroup) {
      promptDeleteTemplateGroup(pendingTemplateGroupAction.templateGroup);
    }

    setPendingTemplateGroupAction(null);
  };

  const showAddTemplateGroupModal: () => void = () => {
    requestTemplateGroupAction('add');
  };

  const showAlert = (
    title: string,
    message?: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertVisible(true);
  };

  const renderTemplateIcon = (template: TemplateItem) => {
    if (template.type === TemplateType.SENSOR) {
      return <SensorIcon fill={Colors.light.hereworksBlue200} />;
    }
    if (template.type === TemplateType.METER) {
      return <MeterIcon stroke={Colors.light.hereworksBlue200} />;
    }
    return <TemplateIcon24
      stroke={Colors.light.hereworksBlue200}
      color='transparent'
    />;
  };

  const attemptTemplateDeletion = (templateToDelete: TemplateItem | null) => {
    if (!templateToDelete?.id) return;
    setDeletingTemplate(true);
    setDeletingTemplateId(templateToDelete.id);
    deleteTemplate(templateToDelete.id).then((res: any) => {
      if (res && !res.isError) {
        removeTemplateLocally(templateToDelete.id);
        setOptionsSelectedTemplate(null);
        showAlert(
          'Template was successfully deleted',
          `${templateToDelete.name} was successfully deleted`,
          false,
        );
      } else {
        showAlert('Error', res?.errorText ?? DELETE_TEMPLATE_FAILURE);
      }
    }).catch((error) => {
      showAlert('Error', error?.message ?? DELETE_TEMPLATE_FAILURE);
    }).finally(() => {
      setDeletingTemplate(false);
      setDeletingTemplateId(null);
    });
  };

  const promptDeleteTemplate = (templateToDelete: TemplateItem | null) => {
    if (!templateToDelete) return;
    showAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{templateToDelete.name}</Text>
      </>,
      true,
      () => attemptTemplateDeletion(templateToDelete),
      'Delete',
    );
  };

  const attemptTemplateGroupDeletion = () => {
    if (!templateGroupToDelete?.ButtonPrototypeGroupId) return;
    setDeletingTemplate(true);
    updateTemplateGroup(
      null,
      templateGroupToDelete.ButtonPrototypeGroupId,
      true,
    ).then((res: any) => {
      if (res && !res.isError) {
        updateTemplateGroupLocally(null, templateGroupToDelete.ButtonPrototypeGroupId, false, true);
        if (selectedTemplateGroup?.ButtonPrototypeGroupId
          === templateGroupToDelete.ButtonPrototypeGroupId) {
          setSelectedTemplateGroup(null);
        }
        showAlert(
          DELETE_TEMPLATE_GROUP_SUCCESS,
          `${templateGroupToDelete.Name} was successfully deleted`,
          false,
        );
        setTemplateGroupToDelete(null);
      } else {
        showAlert('Error', res?.errorText ?? 'Could not delete template group. Please try again later.');
      }
    }).catch((error) => {
      showAlert('Error', error?.message ?? 'Could not delete template group. Please try again later.');
    }).finally(() => setDeletingTemplate(false));
  };

  const editTemplateGroup = (templateGroup: TemplateGroup | null) => {
    if (!templateGroup) return;
    requestTemplateGroupAction('edit', templateGroup);
  };

  const promptDeleteTemplateGroup = (templateGroup: TemplateGroup | null) => {
    if (!templateGroup) return;
    setTemplateGroupToDelete(templateGroup);
    showAlert(
      'Delete template group',
      <>
        {'Are you sure you want to delete the template group '}
        <Text style={Style.alertBoldText}>{templateGroup.Name}</Text>
      </>,
      true,
      attemptTemplateGroupDeletion,
      'Delete',
    );
  };

  const onTemplateOptionsModalHide: () => void = () => {
    if (!pendingTemplateAction) return;

    if (pendingTemplateAction.type === 'edit') {
      setTemplateToUpdate(pendingTemplateAction.template);
    } else {
      promptDeleteTemplate(pendingTemplateAction.template);
    }

    setPendingTemplateAction(null);
  };

  const templateListData: TemplateItem[] = ((selectedTemplateGroup
    ?.buttonPrototypes as TemplateItem[]) ?? templates)
    ?.filter((template: TemplateItem) => template
      .name.toLowerCase().includes(templateSearchValue.trim().toLowerCase()))
    ?.sort((prev: Template, next: Template) => prev.name?.toLowerCase()
      .localeCompare(next.name?.toLowerCase()));

  if (templatesLoading) {
    return <View style={[styles.container, styles.loadingContainer, { paddingTop: top }]}>
      <ActivityIndicator size='large' color={Colors.light.hereworksBlue500} />
      <Text style={styles.loadingText}>{'Loading templates...'}</Text>
    </View>;
  }

  if (templateDetailsShown) {
    return <View style={[styles.container, { paddingTop: top }]}>
      <TemplateDetailsScreen
        template={templateDetailsShown}
        templateGroups={templateGroups}
        userGroups={userGroups}
        onBack={() => setTemplateDetailsShown(null)}
        canEditTemplate={canUpdateTemplates}
        canDeleteTemplate={canDeleteTemplates}
        onEditTemplate={(template: TemplateItem) => {
          setTemplateDetailsShown(null);
          setTemplateToUpdate(template);
        }}
        onDeleteTemplate={(template: TemplateItem) => {
          setTemplateDetailsShown(null);
          promptDeleteTemplate(template);
        }}
      />
    </View>;
  }

  return <View style={[styles.container, { paddingTop: top }]}>
    <View style={styles.screenHeader}>
      <Text style={styles.screenTitle}>
        {selectedTemplateGroup?.Name ?? 'All templates'}
      </Text>
      <PressableStyled onPress={showTemplateGroupSelection}>
        <FontAwesome
          color={styles.screenTitle.color}
          name='angle-down'
          size={24}
        />
      </PressableStyled>
    </View>
    <View style={styles.content}>
      <Text style={styles.subTitle}>
        {`${templateListData?.length ?? '-'} template${templateListData?.length === 1 ? '' : 's'}`}
      </Text>
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchBar}
          placeholder='Search templates'
          value={templateSearchValue}
          onChangeText={setTemplateSearchValue}
        />
        <SearchIcon16 style={styles.searchIcon} />
      </View>
      <FlatList
        style={styles.list}
        data={templateListData}
        keyExtractor={(template: TemplateItem) => `template_${template.id}`}
        renderItem={({ item: template }) => <View style={styles.templateItem}>
          <PressableStyled
            style={styles.templateMainPressArea}
            onPress={() => setTemplateDetailsShown(template)}
          >
            <View style={styles.iconContainer}>
              {renderTemplateIcon(template)}
            </View>
            <View style={styles.templateDetails}>
              <Text style={styles.templateName}>{template.name}</Text>
              {selectedTemplateGroup ? <Text style={styles.templateType}>
                {TemplateTypeTitles[template.type]}
              </Text> : <View style={styles.templateGroupLabel}>
                <Text
                  style={styles.groupName}
                  numberOfLines={1}
                  ellipsizeMode='tail'
                >
                  {template.templateGroupName}
                </Text>
              </View>}
            </View>
          </PressableStyled>
          {(canUpdateTemplates || canDeleteTemplates) && <View style={styles.optionsButton}>
            {deletingTemplateId === template.id ? <ActivityIndicator
              size='small'
              color={Colors.light.hereworksBlue400}
            /> : <PressableStyled
              onPress={() => setOptionsSelectedTemplate(template)}
            >
              <EllipsisIcon fill={Colors.light.hereworksBlue400} />
            </PressableStyled>}
          </View>}
        </View>}
      />
      {canAddTemplate && <Button
        style={styles.addTemplateButton}
        onPress={() => setAddTemplateModalVisible(true)}
        title='Add template'
        children={<PlusIcon style={styles.plusIcon} />}
        affirmative
      />}
    </View>
    <SelectionModal
      style={styles.modal}
      scrollEnabled={false}
      title='Select template group'
      isVisible={templateGroupSelectionShown}
      hide={hideTemplateGroupSelection}
      onModalHide={onTemplateGroupSelectionModalHide}
      listContent={<ModalContent
        {...{
          templateGroups,
          setSelectedTemplateGroup,
          hideTemplateGroupSelection,
          canAddTemplateGroup,
          canEditTemplateGroup: canUpdateTemplates,
          canDeleteTemplateGroup: canDeleteTemplates,
          onEditTemplateGroup: editTemplateGroup,
          onDeleteTemplateGroup: (templateGroup: TemplateGroup | null) => {
            if (!templateGroup) return;
            requestTemplateGroupAction('delete', templateGroup);
          },
          onAddTemplateGroup: showAddTemplateGroupModal,
        }}
      />}
    />
    <SelectionModal
      isVisible={!!optionsSelectedTemplate}
      hide={() => setOptionsSelectedTemplate(null)}
      onModalHide={onTemplateOptionsModalHide}
      title={optionsSelectedTemplate?.name ?? 'Template options'}
      listContent={<>
        {canUpdateTemplates && <Pressable
          style={({ hovered, pressed }) => [
            styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            setPendingTemplateAction({ type: 'edit', template: optionsSelectedTemplate });
            setOptionsSelectedTemplate(null);
          }}
        >
          <Text style={styles.optionText}>{'Edit'}</Text>
        </Pressable>}
        {canDeleteTemplates && <Pressable
          style={({ hovered, pressed }) => [
            styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
          ]}
          onPress={() => {
            setPendingTemplateAction({ type: 'delete', template: optionsSelectedTemplate });
            setOptionsSelectedTemplate(null);
          }}
        >
          <Text style={styles.optionText}>{'Delete'}</Text>
        </Pressable>}
      </>}
    />
    <AddTemplateModal
      {...{ templateGroups }}
      defaultTemplateGroupID={selectedTemplateGroup?.ButtonPrototypeGroupId}
      isVisible={addTemplateModalVisible}
      hide={() => setAddTemplateModalVisible(false)}
      update={updateTemplateLocally}
      showParentAlert={showScreenAlert}
    />
    <AddTemplateModal
      {...{ templateGroups }}
      isVisible={!!templateToUpdate}
      hide={() => setTemplateToUpdate(null)}
      templateToUpdate={templateToUpdate}
      update={updateTemplateLocally}
      showParentAlert={showScreenAlert}
    />
    <AddTemplateGroupModal
      {...{ templateGroups }}
      isVisible={addTemplateGroupModalVisible}
      hide={() => setAddTemplateGroupModalVisible(false)}
      update={updateTemplateGroupLocally}
    />
    <AddTemplateGroupModal
      {...{ templateGroups }}
      isVisible={!!templateGroupToUpdate}
      hide={() => setTemplateGroupToUpdate(null)}
      id={templateGroupToUpdate?.ButtonPrototypeGroupId}
      currentName={templateGroupToUpdate?.Name ?? ''}
      update={updateTemplateGroupLocally}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      loading={deletingTemplate}
      setModalVisible={setAlertVisible}
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    paddingTop: 24,
    backgroundColor: Colors.light.hereworksBackground,
    flex: 1,
  },
  content: {
    marginTop: 12,
    backgroundColor: '#FFF',
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 8,
  },
  iconContainer: {
    width: 40,
    aspectRatio: 1,
    borderRadius: 100,
    backgroundColor: Colors.light.hereworksBlue50,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  groupName: {
    fontSize: 12,
    letterSpacing: 0,
    fontFamily: 'Roboto_400Regular',
    color: '#FFF',
  },
  list: {
    marginHorizontal: -20,
    paddingHorizontal: 20,
  },
  modal: {
    maxHeight: 469,
  },
  screenHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 24,
  },
  addTemplateButton: {
    marginTop: 12,
    height: 36,
    flexDirection: 'row-reverse',
    alignSelf: 'stretch',
    flex: undefined,
  },
  plusIcon: {
    marginEnd: 8,
  },
  screenTitle: {
    fontSize: 22,
    fontFamily: 'Poppins_600SemiBold',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack600,
    marginEnd: 8,
  },
  searchBar: {
    paddingStart: 36,
    paddingEnd: 12,
    paddingVertical: 8,
    borderWidth: 1.5,
    borderRadius: 4,
    borderColor: Colors.light.hereworksBlue50,
    marginVertical: 8,
  },
  searchContainer: {
    justifyContent: 'center',
  },
  searchIcon: {
    position: 'absolute',
    start: 12,
  },
  subTitle: {
    fontSize: 12,
    fontFamily: 'Roboto_500Medium',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack400,
    textTransform: 'uppercase',
    marginTop: 8,
  },
  templateItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    padding: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue200,
    flex: 1,
  },
  templateDetails: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
  },
  templateMainPressArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  templateGroupLabel: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  templateName: {
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
    letterSpacing: 0,
    flexWrap: 'wrap',
    flexShrink: 1,
    maxWidth: '100%',
  },
  templateType: {
    fontSize: 12,
    fontFamily: 'Roboto_400Regular',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
  },
  optionsButton: {
    marginStart: 8,
    padding: 8,
  },
  option: {
    paddingVertical: 8,
    paddingHorizontal: 24,
    marginHorizontal: -24,
  },
  optionText: {
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
  },
});
